import { BondRecordData } from "./FormBondApp.types";

export enum Step {
  INSTRUCTIONS = "instructions",
  START = "start",
  BOND_APPLICANT = "bondApplicant",
  BOND_BUSINESS = "bondBusiness",
  BOND_RISK = "bondRisk",
  BOND_SPECIFIC = "bondSpecific",
  BOND_INDEMNITOR = "bondIndemnitor",
  BOND_OPTIONS = "bondOptions",
  PROCESSING = "processing",
  COMPLETE = "complete",
  BOND_DELIVERY = "bondDelivery",
  BOND_PREVIEW = "bondPreview",
  BOND_AGENCY_QUESTIONS = "bondAgencyQuestions",

  // external products skip rating and credit, then rejoin the standard flow
  // once submitted
  EXTERNAL_BOND_SPECIFIC = "externalBondSpecific",
  EXTERNAL_BOND_OPTIONS = "externalBondOptions"
}

export const activeSchema = {
  [Step.INSTRUCTIONS]: "",
  [Step.START]: "bondBasicFormSchema",
  [Step.BOND_APPLICANT]: "bondApplicantFormSchema",
  [Step.BOND_BUSINESS]: "bondBusinessFormSchema",
  [Step.BOND_RISK]: "bondRiskAssessmentFormSchema",
  [Step.BOND_SPECIFIC]: "productFormSchema",
  [Step.BOND_INDEMNITOR]: "bondIndemnitorFormSchema",
  [Step.BOND_OPTIONS]: "bondOptionsFormSchema",
  [Step.BOND_DELIVERY]: "",
  [Step.BOND_PREVIEW]: "",
  [Step.PROCESSING]: "",
  [Step.COMPLETE]: "",
  [Step.BOND_AGENCY_QUESTIONS]: "bondAgencyQuestionsFormSchema",

  // built from the product's external_product rather than fetched into
  // BondAppFormContext, so these keys only gate rendering
  [Step.EXTERNAL_BOND_SPECIFIC]: "externalFormSpecificSchema",
  [Step.EXTERNAL_BOND_OPTIONS]: "externalBondOptionsSchema"
};

export const marketingDataParams = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "aff_t",
  "aff_c"
];

export const schema = {
  start: [
    "first_name",
    "middle_name",
    "last_name",
    "suffix",
    "is_business"
    // "",
  ],
  bondBusiness: [
    "applicant_first_name",
    "applicant_middle_name",
    "applicant_last_name",
    "applicant_suffix",
    "is_business",
    "business_name",
    "business_dba",
    "business_type_id",
    "email",
    "phone",
    "fax",
    "mailing_street",
    "mailing_city",
    "mailing_state",
    "mailing_zip",
    "business_fein_number"
    // "",
  ],
  bondApplicant: [
    "first_name",
    "middle_name",
    "last_name",
    "suffix",
    "is_business",
    "phone",
    "email",
    "mailing_street",
    "mailing_city",
    "mailing_state",
    "mailing_zip"
    // "",
  ],
  bondRisk: ["is_felon", "has_previous_claim", "has_open_claim"],
  bondSpecific: [],
  bondIndemnitor: [
    "ownership_percentage",
    "is_us_citizen",
    "is_felon",
    "is_felon_details",
    "has_previous_claim",
    "has_lien",
    "years_of_related_experience",
    "first_name",
    "middle_name",
    "last_name",
    "phone",
    "email",
    "ssn",
    "mailing_street",
    "mailing_city",
    "mailing_state",
    "mailing_zip",
    "credit_pull_authorized"
  ],
  bondOptions: []
} as any;

export const uiSchema = {
  "ui:submitButtonOptions": {
    props: {
      disabled: false,
      className: "submit_button"
    },
    norender: false,
    submitText: "Continue"
  }
};

export const getUiSchemaIndemnitor = (isNoRenderSubmit: boolean) => ({
  "ui:submitButtonOptions": {
    props: {
      className: "submit_button"
    },
    norender: isNoRenderSubmit,
    submitText: "Continue"
  }
});

const PHONE_CONSENT_HELP = (
  <>
    By providing your phone number, you consent to receive communications from BOSS
    Bonds regarding your application or bond, including reminders to complete your
    application or renewal notices. Such communications may be delivered using
    automated or artificial voice technology. For additional details, including
    opt-out instructions, see "IX. Consent to Automated Communications" in our{" "}
    <a
      href="https://www.bossbonds.com/terms-and-conditions"
      target="_blank"
      rel="noopener noreferrer"
    >
      Terms and Conditions
    </a>
    .
  </>
);

export const getUiSchemaPrincipal = () => ({
  ...getUiSchemaIndemnitor(false),
  phone: {
    "ui:help": PHONE_CONSENT_HELP
  }
});

export const TERMS_AND_CONDITIONS_VERSION = "2026-03-18";

/**
 * The consent gate every submit path shows before a bond is ordered.
 */
export const termsAndConditionsProperties = () => ({
  agreed_to_terms_and_conditions: {
    type: "boolean",
    title:
      "I acknowledge that I have read and agree to the BOSS Bonds Terms and Conditions.",
    readOnly: false
  },
  tc_version: {
    type: "string",
    default: TERMS_AND_CONDITIONS_VERSION
  }
});

const TERMS_AND_CONDITIONS_HELP = (
  <a
    href="https://www.bossbonds.com/terms-and-conditions"
    target="_blank"
    rel="noopener noreferrer"
  >
    Read the Terms and Conditions.
  </a>
);

export const getUiSchemaBondOptions = () => ({
  ...getUiSchemaIndemnitor(false),
  agreed_to_terms_and_conditions: {
    "ui:widget": "SingleCheckboxWidget",
    "ui:label": false,
    "ui:help": TERMS_AND_CONDITIONS_HELP
  },
  tc_version: {
    "ui:widget": "hidden"
  }
});

export const getUiSchemaForStep = (
  step: Step,
  agencyQuestionsUiSchema: any
) => {
  if (step === Step.BOND_APPLICANT || step === Step.BOND_BUSINESS) {
    return getUiSchemaPrincipal();
  }
  if (step === Step.BOND_OPTIONS) {
    return getUiSchemaBondOptions();
  }
  return agencyQuestionsUiSchema || undefined;
};

export const mappingWarnings = {
  AL: [
    "Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or who knowingly presents false information in an application for insurance is guilty of a crime and may be subject to restitution, fines, or confinement in prison, or any combination thereof.",
    "Workers Compensation: Any person who makes or causes to be made any knowingly false or fraudulent material statement or material representation for the purpose of obtaining workers compensation benefits for himself or herself or any other person is guilty of a Class C felony."
  ],
  AK: [
    "A person who knowingly and with intent to injure, defraud, or deceive an insurance company files a claim containing false, incomplete, or misleading information may be prosecuted under state law."
  ],
  AZ: [
    "For your protection Arizona law requires the following statement to appear on this form: Any person who knowingly presents a false or fraudulent claim for payment of a loss is subject to criminal and civil penalties."
  ],
  AR: [
    "Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison.",
    "Workers Compensation: Any person or entity who willfully and knowingly makes any material false statement or representation, who willfully and knowingly omits or conceals any material information, or who willfully and knowingly employs any device, scheme or artifice, for the purpose of obtaining any benefit or payment, defeating or wrongfully increasing or wrongfully decreasing any claim for benefit or payment, or obtaining or avoiding workers compensation coverage or avoiding payment of the proper insurance premium, or who aids and abets for any of said purposes, under this chapter will be guilty of a Class D felony."
  ],
  CA: [
    "For your protection, California law requires the following to appear on this form: Any person who knowingly presents a false or fraudulent claim for the payment of a loss is guilty of a crime and may be subject to fines and confinement in state prison.",
    "Workers Compensation: Any person who makes or causes to be made any knowingly false or fraudulent material statement or material representation for the purpose of obtaining or denying workers compensation benefits or payments is guilty of a felony."
  ],
  CO: [
    "It is unlawful to knowingly provide false, incomplete, or misleading facts or information to an insurance company for the purpose of defrauding or attempting to defraud the company. Penalties may include imprisonment, fines, denial of insurance, and civil damages. Any insurance company or agent of an insurance company who knowingly provides false, incomplete, or misleading facts or information to a policyholder or claimant for the purpose of defrauding or attempting to defraud the policyholder or claimant with regard to a settlement or award payable from insurance proceeds shall be reported to the Colorado Division of Insurance within the Department of Regulatory Agencies."
  ],
  DE: [
    "Any person who knowingly, and with intent to injure, defraud or deceive any insurer, files a statement of claim containing any false, incomplete or misleading information is guilty of a felony."
  ],
  DC: [
    "WARNING: It is a crime to provide false or misleading information to an insurer for the purpose of defrauding the insurer or any other person. Penalties include imprisonment and/or fines. In addition, an insurer may deny insurance benefits if false information materially related to a claim was provided by the applicant."
  ],
  FL: [
    "Any person who knowingly and with intent to injure, defraud, or deceive any insurer files a statement of claim containing any false, incomplete, or misleading information is guilty of a felony of the third degree.",
    "Workers Compensation: Any person who, knowingly and with intent to injure, defraud, or deceive any employer or employee, insurance company, or self-insured program, files a statement of claim containing any false or misleading information, commits insurance fraud, punishable as provided in s. 817.234."
  ],
  ID: [
    "Any person who knowingly, and with intent to defraud or deceive any insurance company, files a statement containing any false, incomplete, or misleading information is guilty of a felony."
  ],
  IN: [
    "A person who knowingly and with intent to defraud an insurer files a statement of claim containing any false, incomplete, or misleading information commits a felony."
  ],
  KS: [
    "Workers Compensation: Warning: Acceptance of employment with a different employer that requires the performance of activities you have stated you cannot perform because of the injury for which you are receiving temporary disability benefits could constitute fraud and could result in loss of future benefits and restitution of prior workers compensation awards and benefits paid."
  ],
  KY: [
    "Any person who knowingly and with intent to defraud any insurance company or other person files a statement of claim containing any materially false information or conceals, for the purpose of misleading, information concerning any fact material thereto, commits a fraudulent insurance act, which is a crime."
  ],
  LA: [
    "Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison.",
    "Workers Compensation: Failure to answer truthfully may result in forfeiture of workers compensation benefits."
  ],
  ME: [
    "It is a crime to knowingly provide false, incomplete or misleading information to an insurance company for the purpose of defrauding the company. Penalties may include imprisonment, fines or a denial of insurance benefits."
  ],
  MD: [
    "Any person who knowingly or willfully presents a false or fraudulent claim for payment of a loss or benefit or who knowingly or willfully presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison."
  ],
  MN: [
    "A person who files a claim with intent to defraud, or helps commit a fraud against an insurer, is guilty of a crime.",
    "Workers Compensation: Any person who, with intent to defraud, receives workers\u2019 compensation benefits to which the person is not entitled by knowingly misrepresenting, misstating, or failing to disclose any material fact is guilty of theft and shall be sentenced pursuant to s 609.52, subdivision 3."
  ],
  NH: [
    "Any person who, with a purpose to injure, defraud or deceive any insurance company, files a statement of claim containing any false, incomplete or misleading information is subject to prosecution and punishment for insurance fraud, as provided in RSA 638:20."
  ],
  NJ: [
    "Any person who knowingly files a statement of claim containing any false or misleading information is subject to criminal and civil penalties."
  ],
  NM: [
    "Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to civil fines and criminal penalties."
  ],
  NY: [
    "GENERAL: Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance or statement of claim containing any materially false information, or conceals for the purpose of misleading, information concerning any fact material thereto, commits a fraudulent insurance act, which is a crime, and shall also be subject to a civil penalty not to exceed five thousand dollars and the stated value of the claim for each such violation.",
    "AUTO: Any person who knowingly makes or knowingly assists, abets, solicits or conspires with another to make a false report of the theft, destruction, damage or conversion of any motor vehicle to a law enforcement agency, the department of motor vehicles or an insurance company, commits a fraudulent insurance act, which is a crime, and shall also be subject to a civil penalty not to exceed five thousand dollars and the value of the subject motor vehicle or stated claim for each violation.",
    "FIRE: Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance containing any false information, or conceals for the purpose of misleading, information concerning any fact material thereto, commits a fraudulent insurance act, which is a crime. The proposed insured affirms that the foregoing information is true and agrees that these applications shall constitute a part of any policy issued whether attached or not and that any willful concealment or misrepresentation of a material fact or circumstances shall be grounds to rescind the insurance policy."
  ],
  OH: [
    "Any person who, with intent to defraud or knowing that he is facilitating a fraud against an insurer, submits an application or files a claim containing a false or deceptive statement is guilty of insurance fraud."
  ],
  OK: [
    "WARNING: Any person who knowingly, and with intent to injure, defraud or deceive any insurer, makes any claim for the proceeds of an insurance policy containing any false, incomplete or misleading information is guilty of a felony.",
    "Workers Compensation Warning: Any person or entity who makes any material false statement or representation, who willfully and knowingly omits or conceals any material information, or who employs any device, scheme, or artifice, or who aids and abets any person for the purpose of: 1. obtaining any benefit or payment, 2. increasing any claim for benefit or payment, or 3. obtaining workers' compensation coverage under this act, shall be guilty of a felony punishable pursuant to Section 1663 of Title 21 of the Oklahoma Statutes."
  ],
  PA: [
    "GENERAL: Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance or statement of claim containing any materially false information or conceals for the purpose of misleading, information concerning any fact material thereto commits a fraudulent insurance act, which is a crime and subjects such person to criminal and civil penalties.",
    "AUTO: Any person who knowingly and with intent to injure or defraud any insurer files an application or claim containing any false, incomplete or misleading information shall, upon conviction, be subject to imprisonment for up to seven years and the payment of a fine of up to $15,000."
  ],
  RI: [
    "Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison."
  ],
  WV: [
    "Any person who knowingly presents a false or fraudulent claim for payment of a loss or benefit or knowingly presents false information in an application for insurance is guilty of a crime and may be subject to fines and confinement in prison."
  ],
  TN: [
    "It is a crime to knowingly provide false, incomplete or misleading information to an insurance company for the purpose of defrauding the company. Penalties include imprisonment, fines and denial of insurance benefits."
  ],
  TX: [
    "Workers Compensation: Any person who knowingly presents a false or fraudulent claim for the payment of a loss is guilty of a crime and may be subject to fines and confinement in state prison."
  ],
  UT: [
    "Workers Compensation: Any person who knowingly presents false or fraudulent underwriting information, files or causes to be filed a false or fraudulent claim for disability compensation or medical benefits, or submits a false or fraudulent report or billing for health care fees or other professional services is guilty of a crime and may be subject to fines and confinement in state prison."
  ],
  VA: [
    "It is a crime to knowingly provide false, incomplete or misleading information to an insurance company for the purpose of defrauding the company. Penalties include imprisonment, fines and denial of insurance benefits."
  ],
  WA: [
    "It is a crime to knowingly provide false, incomplete, or misleading information to an insurance company for the purpose of defrauding the company. Penalties include imprisonment, fines, and denial of insurance benefits."
  ],
  default: [
    "I certify that all information given in this application is correct to the best of my knowledge. Completion of this application constitutes permission to obtain consumer information which may be used to determine bonding eligibility. I understand that knowingly submitting false, incomplete, or misleading information is considered fraud and guilty of a felony."
  ]
};

const Statuses = {
  BOND_ISSUED: {
    title: "Payment successful",
    subTitle: "Thank you for purchasing your bond!",
    extraInfo: [
      "Your electronic bond will be emailed to you shortly. If you entered in shipping information, a wet signed version of your bond will ship via US Postal or FedEx in the next 24 hours (business days only). ",
      "Your bond bears our signature and seals. In some cases, the applicant will need to sign the bond. The bond form will indicate if a witness or notary is required.",
      "Review your bond. Notify us by email if any items are incorrect or misspelled.",
      "Print your bond. Add any necessary signatures. Present the bond to the government agency that has requested the bond. We recommend that you confirm with the agency that an electronic/printed version is acceptable.",
      "Your electronic bond includes a copy of the electronically signed indemnity agreement and validation of the signing. Please retain this information for your records.",
      "If shipping was selected during check-out, we will ship a bond to you that includes a wet-signature of our power of attorney and a pressed seal of the surety company. Some entities will only accept this version of the bond document.",
      "We appreciate your business, and our goal is 100% satisfaction. If you encounter a problem with your bond, please contact us."
    ]
  },
  BOND_ISSUE_READY: {
    title: "Payment successful",
    subTitle: "Thank you for purchasing your bond!",
    extraInfo: [
      "Your electronic bond will be emailed to you shortly. If you entered in shipping information, a wet signed version of your bond will ship via US Postal or FedEx in the next 24 hours (business days only). ",
      "Your bond bears our signature and seals. In some cases, the applicant will need to sign the bond. The bond form will indicate if a witness or notary is required.",
      "Review your bond. Notify us by email if any items are incorrect or misspelled.",
      "Print your bond. Add any necessary signatures. Present the bond to the government agency that has requested the bond. We recommend that you confirm with the agency that an electronic/printed version is acceptable.",
      "Your electronic bond includes a copy of the electronically signed indemnity agreement and validation of the signing. Please retain this information for your records.",
      "If shipping was selected during check-out, we will ship a bond to you that includes a wet-signature of our power of attorney and a pressed seal of the surety company. Some entities will only accept this version of the bond document.",
      "We appreciate your business, and our goal is 100% satisfaction. If you encounter a problem with your bond, please contact us."
    ]
  },
  REMARKET: {
    title: "Thank you, your app is being reviewed",
    subTitle: "This bond needs to be manually reviewed by our team",
    extraInfo: [
      "One of our agents will contact you soon with additional information."
    ]
  },
  UNDERWRITING_REVIEW: {
    title: "Thank you, your app is being reviewed",
    subTitle: "This bond needs to be manually reviewed by our team",
    extraInfo: [
      "One of our agents will contact you soon with additional information."
    ]
  },
  UNDERWRITING_INFO_REQUIRED: {
    title: "Thank you, your app is being reviewed",
    subTitle: "This bond needs to be manually reviewed by our team",
    extraInfo: [
      "One of our agents will contact you soon with additional information."
    ]
  },
  SIGNATURE_REQUIRED: {
    title: "Payment successful",
    subTitle:
      "Sit back and relax! One of our agents will contact you to finish the issuance of your bond"
  },
  PAYMENT_REQUIRED: {
    title: "Congratulations! \nYour bond is ready to be purchased.",
    subTitle: `You can download a sample of your bond below, or click on "BUY NOW" to continue.`
  }
};

const getBondCompleteExtraInfo = (bondData?: BondRecordData): string[] => {
  const isUCSBond: boolean = !!bondData?.is_issued_bond_pdf_available;
  const isRemarketBond: boolean = !bondData?.is_issued_bond_pdf_available;
  const isDigitalBondAccepted: boolean = !bondData?.is_shipping_required;

  if (isDigitalBondAccepted) {
    return [
      "Your electronic bond will be emailed to you shortly. Your bond bears our signature and seals. You can print, sign, and file this electronic bond copy with the Obligee, per the statutes governing the entity requiring the bond. Therefore, no paper copy of the bond will be mailed to you.",
      "Review your bond. Notify us by email if any items are incorrect or misspelled.",
      "Print your bond. Add any necessary signatures. The bond form will indicate if a witness or notary is required. ",
      "Present the bond to the government agency that has requested the bond. ",
      "We appreciate your business, and our goal is 100% satisfaction. If you encounter a problem with your bond, please contact us."
    ]
  } else if (isUCSBond) {
    return [
      "Your electronic bond will be emailed to you shortly. If you entered in shipping information, a signed version of your bond will ship via US Postal or FedEx in the next 24 hours (business days only).",
      "Your bond bears our signature and seals. In some cases, the applicant will need to sign the bond. The bond form will indicate if a witness or notary is required.",
      "Review your bond. Notify us by email if any items are incorrect or misspelled.",
      "Print your bond. Add any necessary signatures. Present the bond to the government agency that has requested the bond. We recommend that you confirm with the agency that an electronic/printed version is acceptable. ",
      "Your electronic bond includes a copy of the electronically signed indemnity agreement and validation of the signing. Please retain this information for your records.",
      "If shipping was selected during check-out, we will ship a bond to you that includes a signature of our power of attorney and a pressed seal of the surety company. Some entities will only accept this version of the bond document.",
      "We appreciate your business, and our goal is 100% satisfaction. If you encounter a problem with your bond, please contact us."
    ]
  } else if (isRemarketBond) {
    return [
      "A signed version of your bond will ship via US Postal or FedEx in the next 24 hours (business days only). The bond includes a signature of our power of attorney and a pressed seal of the surety company",
      "In some cases, the applicant will need to sign the bond. The bond form will indicate if a witness or notary is required.",
      "Review your bond. Notify us by email if any items are incorrect or misspelled. Add any necessary signatures. Present the bond to the government agency that has requested the bond.",
      "We appreciate your business, and our goal is 100% satisfaction. If you encounter a problem with your bond, please contact us."
    ]
  } else {
    // this is to satisfy typescript to prevent undefined. The above cases will always be returned unless there is no
    // BondRecordData, in which case nothing is going to be working.
    return []
  }
}

export enum BondStatuses {
  BOND_ISSUED = "BOND_ISSUED",
  UNDERWRITING_REVIEW = "UNDERWRITING_REVIEW",
  REMARKET = "REMARKET",
  SIGNATURE_REQUIRED = "SIGNATURE_REQUIRED",
  BOND_ISSUE_READY = "BOND_ISSUE_READY",
  PAYMENT_REQUIRED = "PAYMENT_REQUIRED",
  UNDERWRITING_INFO_REQUIRED = "UNDERWRITING_INFO_REQUIRED"
}

export const getBondInfo = ({
  step,
  bondName,
  isEnabled,
  mailingState,
  bondStatus,
  bondData,
  isSkipBondIndemnitor = true,
  isBlocked,
  isOfflineApplication
}: {
  step: Step;
  bondName?: string;
  isEnabled: boolean;
  mailingState: keyof typeof mappingWarnings;
  bondStatus?: BondStatuses;
  bondData?: BondRecordData;
  isSkipBondIndemnitor?: boolean;
  isBlocked: boolean;
  isOfflineApplication?: boolean;
}) => {
  const currentStatus = bondStatus && BondStatuses[bondStatus];

  const bondDisclaimers = {
    instructions: {
      title: isOfflineApplication
        ? "This application cannot be applied for directly online"
        : "Instructions"
    },
    start: {
      title: "Principal Information",
      subTitle: "Enter your personal information",
      extraInfo: [
        `To get started with your ${bondName} bond, enter your personal information. This information will also be used on the bond if needed.`,
        "Your name should appear exactly as it appears on your professional license."
      ]
    },
    bondApplicant: {
      title: "Principal Information",
      subTitle: "Enter your personal information",
      extraInfo: [
        `To get started with your ${bondName} bond, enter your personal information. This information will also be used on the bond if needed.`,
        "Your name should appear exactly as it appears on your professional license."
      ]
    },
    bondBusiness: {
      title: "Principal Information",
      subTitle: "Enter your personal information",
      extraInfo: [
        `To get started with your ${bondName} bond, enter your personal information. This information will also be used on the bond if needed.`,
        "Your name should appear exactly as it appears on your professional license.",
        "Business Name should appear exactly as it appears on your business / professional license.",
        "DBA is only required if it is part of your official business name. Otherwise, you should leave this field blank."
      ]
    },
    bondRisk: {
      title: "History",
      subTitle: "Answer questions",
      extraInfo: ["These fields are required before we can quote your bond."]
    },
    bondSpecific: {
      title: "Bond specific questions",
      subTitle: isEnabled
        ? "Answer bond-specific questions"
        : "No bond-specific fields, you can skip to the next step.",
      extraInfo: ["These fields are required before we can quote your bond."]
    },
    bondIndemnitor: {
      title: "Indemnitors",
      subTitle: isEnabled
        ? "Enter indemnitor information"
        : `${bondName} does not require additional indemnitor information.`,

      extraInfo: [
        !isSkipBondIndemnitor
          ? "You must include all 10% or greater owners and their spouses as an Indemnitor. An indemnitor is anyone who is responsible for losses claimed on the bond."
          : "No additional indemnitor information is required. Click Continue below."
      ],
      submitInfo: [
        isEnabled
          ? "By continuing, you authorize us to perform a soft credit check to evaluate eligibility for this surety bond and any future bond-related services, including renewals and additional bond requests. This is a “soft” inquiry and, in most cases, will not affect your credit score."
          : "No action required, you can skip to the next step"
      ]
    },
    bondOptions: {
      title: "Bond Options",
      subTitle: "Almost there!",
      extraInfo: isEnabled
        ? [`These fields are required before we can quote your bond.`]
        : [`${bondName} bonds are effective as of the date the bond is issued`]
    },
    bondDelivery: {
      title: "Shipping Options"
    },
    complete: {
      title: "Payment successful",
      subTitle: "Thank you for purchasing your bond!",
      extraInfo: getBondCompleteExtraInfo(bondData)
    },
    processing: {
      title: "Thank you!",
      subTitle:
        bondData?.status_name === "BOND_ISSUE_READY"
          ? "We are processing - please wait and do not refresh your browser"
          : bondData?.status_name === "SIGNATURE_REQUIRED"
          ? "We are almost there! As a last step, our agents will reach out to you to receive signatures on the General Indemnity Agreement required for this bond, prior to issuance. "
          : "Your application is being reviewed"
    },
    bondPreview: {
      title: "Congratulations! \nYour bond is ready to be issued!",
      subTitle: bondData?.is_bond_sample_pdf_available
        ? 'You can download a sample of your bond below, or click on "BUY NOW" to continue'
        : 'Click on "BUY NOW" to continue'
    },
    bondAgencyQuestions: {
      title: "Answer bond-specific questions",
      subTitle: "These fields are required before we can quote your bond."
    }
  };
  const stateDisclaimer =
    mappingWarnings[mailingState] || mappingWarnings.default;

  if (isBlocked) {
    return {
      title: "This application is no longer available.",
      subTitle: "Something went wrong, please contact support"
    };
  }

  // Handle the case where there isn't a valid payment method (GIGSBM-1322)
  if(bondData?.status_name === 'PAYMENT_REQUIRED' && ! bondData?.salesforce_payment_link) {
    return {
        title: bondDisclaimers['bondPreview'].title,
        subTitle: "",
      };
  }

  // the external flow has its own copy for its own steps
  return {
    ...bondDisclaimers[step as keyof typeof bondDisclaimers],
    stateDisclaimer
  };
};

export const UI_SCHEMA_ELEMS_TO_DELETE = [
  "radio",
  "select",
  "datepicker",
  "input",
  "date",
  "checkbox"
];
